import { clsx } from 'clsx';
import type { ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

/**
 * Returns Tailwind classes for properly styling Quill editor content
 * Handles bullet lists that Quill outputs as <ol data-list="bullet">
 */
export function getQuillProseClasses(additionalClasses?: string): string {
    return cn(
        'prose prose-sm text-foreground max-w-none',
        '*:text-foreground',
        // Handle Quill indent classes
        '[&_.ql-indent-1]:pl-[3em]',
        '[&_.ql-indent-2]:pl-[6em]',
        '[&_.ql-indent-3]:pl-[9em]',
        '[&_.ql-indent-4]:pl-[12em]',
        '[&_.ql-indent-5]:pl-[15em]',
        '[&_.ql-indent-6]:pl-[18em]',
        '[&_.ql-indent-7]:pl-[21em]',
        '[&_.ql-indent-8]:pl-[24em]',
        // Handle table styling
        '[&_table]:border-border [&_table]:w-full [&_table]:border-collapse [&_table]:border',
        '[&_th]:border-border [&_th]:bg-muted [&_th]:border [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-medium',
        '[&_td]:border-border [&_td]:border [&_td]:px-3 [&_td]:py-2',
        additionalClasses,
    );
}

/**
 * Converts HSL values to hex color
 */
function hslToHex(h: number, s: number, l: number): string {
    l /= 100;
    const a = (s * Math.min(l, 1 - l)) / 100;
    const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);

        return Math.round(255 * color)
            .toString(16)
            .padStart(2, '0');
    };

    return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Gets a CSS custom property value from document root and converts it to hex color
 * Works with Tailwind's HSL format (e.g., "0 0% 100%")
 */
export function getCssVariableAsHex(variable: string, fallback: string): string {
    const value = getComputedStyle(document.documentElement).getPropertyValue(variable).trim();

    if (!value) {
        return fallback;
    }

    // Parse HSL values like "0 0% 100%" or "hsl(0 0% 100%)"
    // eslint-disable-next-line security/detect-unsafe-regex -- bounded `\d+` groups over the app's own theme CSS values, not attacker input; no nested ambiguity.
    const hslMatch = value.match(/(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/);

    if (hslMatch) {
        const h = parseFloat(hslMatch[1]);
        const s = parseFloat(hslMatch[2]);
        const l = parseFloat(hslMatch[3]);

        return hslToHex(h, s, l);
    }

    return fallback;
}
